import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useBi } from '@wix/yoshi-flow-editor';
import { groupsSearchSearchResultSrc111Evid523 } from '@wix/bi-logger-groups-data/v2';

import {
  selectFeedSearchItems,
  selectFeedSearchMetadata,
  selectFeedResult,
} from 'store/selectors';

import { Box } from 'wui/Box';
import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';
import { List } from 'wui/List';
import { Divider } from 'wui/Divider';

import { FeedResultsList } from './FeedResultsList';
import { FeedItemSearchResultSkeleton } from './FeedItemSearchResult';

import { FeedSearchConfig } from './types';
import { DATA_HOOKS } from './dataHooks';

interface IProps {
  query?: string;
  container: HTMLElement | null;

  onReset(): void;

  origin: string;

  // undefined for central feed, string for group feed
  groupId?: string;

  searchConfig: FeedSearchConfig;
  'data-hook'?: string;
}

export function FeedSearchResults(props: IProps) {
  const {
    groupId,
    query,
    container,
    onReset,
    origin,
    searchConfig,
    'data-hook': dataHook,
  } = props;

  const bi = useBi();

  const { isError, isLoading, isLoadingMore } = useSelector(
    selectFeedSearchMetadata,
  );
  const items = useSelector(selectFeedSearchItems);
  const result = useSelector(selectFeedResult);

  useEffect(() => {
    if (query) {
      searchConfig.onSearch(query);
    }
  }, [query, groupId]);

  useEffect(() => {
    if (isError) {
      bi.report(
        groupsSearchSearchResultSrc111Evid523({
          origin,
          groupId,
          inputText: query,
          responseStatus: 'error',
          tabName: 'posts',
        }),
      );
    }
  }, [isError]);

  if (!query) {
    return (
      <EmptyState
        data-hook={dataHook}
        data-status={DATA_HOOKS.DATA_STATUS_EMPTY_QUERY_ATTR}
        variant="section"
        title={searchConfig.emptyTitle}
        subtitle={searchConfig.emptySubtitle}
        role="status"
        aria-live="polite"
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        direction="vertical"
        role="status"
        aria-busy="true"
        data-hook={dataHook}
        data-status={DATA_HOOKS.DATA_STATUS_LOADING_ATTR}
      >
        <List disablePadding separator={<Divider bw inset />}>
          <FeedItemSearchResultSkeleton />
          <FeedItemSearchResultSkeleton />
          <FeedItemSearchResultSkeleton />
        </List>
      </Box>
    );
  }

  if (isError) {
    return (
      <ErrorState
        bw
        variant="section"
        data-hook={dataHook}
        data-status={DATA_HOOKS.DATA_STATUS_ERROR_ATTR}
        onRetry={handleRetry}
        buttonProps={{ variant: 'basic', outlined: true }}
        title={searchConfig.errorTitle}
        subtitle={searchConfig.errorSubtitle}
        actionLabel={searchConfig.actionLabel}
        role="alert"
        aria-live="assertive"
      />
    );
  }

  return (
    <FeedResultsList
      query={query}
      groupId={groupId}
      data-hook={dataHook}
      data-status={DATA_HOOKS.DATA_STATUS_SUCCESS_ATTR}
      origin={origin}
      onReset={onReset}
      container={container}
      onLoadMore={handleLoadMore}
    />
  );

  function handleRetry() {
    searchConfig.onSearch(query!);
  }

  function handleLoadMore() {
    if (isLoadingMore || !result.hasNext) {
      return;
    }

    searchConfig.onSearchMore(
      query!,
      groupId ? items.length : Number(result.nextCursor),
    );
  }
}

FeedSearchResults.displayName = 'FeedSearchResults';
